/**
 * operators + company_sites + site_tanks + site_geofences (DB-05).
 *
 * The SITE side of the dispatch graph. Phases 5 (job lifecycle), 6 (run-ticket
 * capture), and 8 (geofencing) all read from these tables. Schema is derived
 * from master spec §13 (TANK MANAGEMENT — SiteTank columns), §15 (GEOFENCING),
 * §36 (SITE GEOFENCE TABLE), §37 (api_number — OK Corporation Commission /
 * TX Railroad Commission permit), and §49 (operators are E&P companies that
 * own wellsites).
 *
 * Tables (4 total — all tenant-scoped, all RLS-enforced):
 *   - operators: hauler-tenant-scoped catalog of E&P customers; optional
 *     `linked_operator_tenant_id` cross-link makes the operator self-portal-
 *     usable (Phase 15).
 *   - company_sites: physical wellsites/facilities owned by an operator;
 *     master spec §37 api_number (compliance ID) with partial UNIQUE.
 *   - site_tanks: RETIRED by Phase 26.2 (DEC-147); tanks live on
 *     hauler_tanks (platform-catalog.ts). Phase 13 computes fill rates on
 *     read from haul_capture_tanks readings and adds no columns here
 *     (DEC-348, .planning/phases/13-tank-management/13-PLAN.md).
 *   - site_geofences: master spec §36 polygon geofences. site_id is
 *     POLYMORPHIC per master spec §86 — when site_type='YARD' it references
 *     yards.yard_id; when 'PICKUP' company_sites.id; when 'DISPOSAL'
 *     disposal_facilities.id (Plan 02-04). Polymorphism is enforced at the
 *     application layer (Phase 8 GEO-02), not via DB FK constraint — this
 *     trade-off is acceptable for the Phase 2 scope.
 *
 * Polygon storage: `polygon_geojson` is stored as a TEXT column holding a
 * GeoJSON-shaped JSON string (e.g. `[[lng1,lat1],[lng2,lat2],...,[lng1,lat1]]`
 * — a closed ring). Drizzle 0.45.x has no first-class POLYGON / PostGIS column
 * type; text + Phase 8 Zod refinement is the documented choice. Phase 23 may
 * upgrade to a real POLYGON / PostGIS geometry if warranted.
 *
 * RLS posture (CONTEXT D-15): every table here is tenant-scoped. Each gets a
 * pgPolicy with the standard
 *   `tenant_id = current_setting('app.current_tenant_id')::uuid`
 * predicate. 4 pgPolicy calls total: operators, company_sites, site_tanks,
 * site_geofences.
 *
 * OPEN-001 deferral: NO `color_cut_options` lookup table here. Master spec
 * §13 has no schema column for color-cut classification; OPEN-001 documents
 * a placeholder UI list (`['None','Trace','1 in','2 in','3 in','4 in','Custom']`)
 * that is configurable in Phase 6 once real-world hauler input arrives. If
 * Phase 6 decides a stored color-cut value is needed on haul_captures, that
 * plan adds the column via a new migration. Phase 2 leaves zero references.
 *
 * Sources:
 *   - master spec §13 (TANK MANAGEMENT — SiteTank columns)
 *   - master spec §15 (GEOFENCING — multi-table relationships)
 *   - master spec §36 (SITE GEOFENCE TABLE — polygon shape)
 *   - master spec §37 (VEHICLE AND DRIVER COMPLIANCE FIELDS — api_number per site)
 *   - master spec §49 (OPERATORS — E&P companies; foreman role assignment)
 *   - master spec §86 (YARDS — site_geofences.site_type='YARD' references yards.yard_id)
 *   - CONTEXT D-15, D-16 (RLS + role split)
 *   - CONTEXT D-18 / OPEN-001 (color-cut placeholder deferral to Phase 6)
 *   - RESEARCH Performance Indexes #24-26 (company_sites: 2; site_tanks: 1)
 *   - RESEARCH §OPEN-001 (lines 325-332 — explicit Phase 2 deferral)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - site_geofences.site_type CHECK allowlist is exactly 3 values:
 *       PICKUP / DISPOSAL / YARD (master spec §86 explicit)
 *   - company_sites.api_number is partial UNIQUE per (tenant_id, api_number)
 *     WHERE api_number IS NOT NULL — required for OK/TX compliance lookups.
 *   - polygon storage is TEXT/GeoJSON JSON string, NOT PostGIS POLYGON.
 *
 * DEFERRED FK CONSTRAINTS — NOT added in this file:
 *   - site_geofences.site_id has NO `.references()` — polymorphic per §86
 *     (resolves to yards.yard_id / company_sites.id / disposal_facilities.id
 *     depending on site_type). Phase 8 GEO-02 enforces polymorphism at the
 *     application layer.
 */

import {
  bigint,
  boolean,
  check,
  foreignKey,
  index,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { importBatches } from './imports.js';

/**
 * operators — E&P companies (master spec §49).
 *
 * Each hauler tenant maintains its own catalog of operator customers. The
 * optional `linked_operator_tenant_id` FK cross-links a hauler-side operator
 * row to a real Operator-typed tenant in the platform — when set, that
 * operator can use its own self-service portal (Phase 15) and still appear
 * in the hauler's customer list. NULL when the operator is hauler-internal
 * only (no portal access).
 *
 * RLS — standard predicate. T-02-03-01 (HIGH) — cross-tenant visibility
 * blocked by RLS; proven by Plan 02-06's RLS-isolation test.
 *
 * Phase 06.2 / DEC-032 #1 — billing-clock + pricing + per-contract flag columns:
 *   - billingStart / billingEnd: nullable TEXT — when NULL, falls back to
 *     tenant.billingStart / tenant.billingEnd via COALESCE (DEC-032 #3).
 *     Values: 'ARRIVAL'|'START' for start; 'DEPARTURE'|'COMPLETION' for end.
 *   - hourlyPriceCents / bidPriceCents: nullable INTEGER >= 0 — per-operator
 *     price overrides for HOURLY / BID billing methods (Phase 12).
 *   - requireReferenceId: BOOLEAN NOT NULL DEFAULT false — when true, capture
 *     handler (Plan 06.2-16) gates ticket submission until driver enters a
 *     reference/PO/order number; stored on jobs.reference_id (BILLCFG-04).
 *   - allowLeaseChange: BOOLEAN NOT NULL DEFAULT true — mobile read-only gate
 *     (Plan 06.2-mobile) hides the "change lease" button when false.
 */
export const operators = pgTable(
  'operators',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Display name shown in dispatch UI.
    name: text('name').notNull(),
    // Legal entity name for invoicing (Phase 12); may equal `name`.
    legalName: text('legal_name'),
    // Cross-tenant link to a self-portal-using Operator-typed tenant; NULL =
    // hauler-internal-only customer. Phase 15 uses this to grant the operator
    // tenant read access to its own jobs via a separate cross-tenant policy.
    linkedOperatorTenantId: uuid('linked_operator_tenant_id').references(() => tenants.id, {
      onDelete: 'set null',
    }),
    // Phase 6 / D-22 step 1 — per-tenant unique 3-letter (varchar(6)) prefix
    // used by issueHaulId() to compose the canonical {abbrev}{9-digit} HaulId.
    // Backfilled by migration 0005 from `name` via the §13 algorithm; partial
    // unique index `operators_tenant_abbrev_idx` enforces uniqueness per tenant.
    customerAbbreviation: varchar('customer_abbreviation', { length: 6 }).notNull(),
    isActive: boolean('is_active').notNull().default(true),
    // Wave 3 / Quick 260615-uf9 — optional operator contact info captured by the
    // dispatcher "create operator" form (migration 0027). All nullable.
    contactName: text('contact_name'),
    contactPhone: text('contact_phone'),
    contactEmail: text('contact_email'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Phase 06.2 / DEC-032 #1 — billing-clock policy override (nullable = fall
    // back to tenant default via COALESCE; DEC-032 #3). CHECK constraints below.
    billingStart: text('billing_start'),
    billingEnd: text('billing_end'),
    // Per-operator price overrides for HOURLY / BID billing (Phase 12). Nullable
    // until set; CHECK ensures non-negative cents. DEC-032 #1.
    hourlyPriceCents: integer('hourly_price_cents'),
    bidPriceCents: integer('bid_price_cents'),
    // Phase 12 / Task 12.0 (D3) — per-operator per-BBL rate override. Nullable =
    // fall back to job_types.default_rate_cents via COALESCE. CHECK non-negative below.
    perBblPriceCents: integer('per_bbl_price_cents'),
    // BILLCFG-04 / DEC-032 #1: when true, capture handler (Plan 06.2-16) requires
    // driver to enter a reference/PO number (stored on jobs.reference_id).
    requireReferenceId: boolean('require_reference_id').notNull().default(false),
    // Mobile gate (Plan 06.2-mobile): hides "change lease" button when false.
    allowLeaseChange: boolean('allow_lease_change').notNull().default(true),
  },
  (t) => [
    index('operators_tenant_idx').on(t.tenantId),
    // One operator-name per hauler — prevents duplicate "ABC Energy" entries.
    uniqueIndex('operators_tenant_name_idx').on(t.tenantId, t.name),
    // Phase 6 / migration 0005 — per-tenant UNIQUE abbreviation. Partial
    // WHERE clause matches the SQL migration: the index is built only on rows
    // with a non-NULL abbreviation (the column is NOT NULL after backfill,
    // but the partial-where mirrors the SQL exactly for schema-diff parity).
    uniqueIndex('operators_tenant_abbrev_idx')
      .on(t.tenantId, t.customerAbbreviation)
      .where(sql`customer_abbreviation IS NOT NULL`),
    pgPolicy('operators_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
    // DEC-032 #1 / T-06.2-11 — billing_start/billing_end allowlist (text + CHECK pattern per DEC-024).
    check(
      'operators_billing_start_check',
      sql`billing_start IS NULL OR billing_start IN ('ARRIVAL','START')`,
    ),
    check(
      'operators_billing_end_check',
      sql`billing_end IS NULL OR billing_end IN ('DEPARTURE','COMPLETION')`,
    ),
    // DEC-032 #1 / T-06.2-12 — price columns must be non-negative when set.
    check(
      'operators_hourly_price_nonneg',
      sql`hourly_price_cents IS NULL OR hourly_price_cents >= 0`,
    ),
    check('operators_bid_price_nonneg', sql`bid_price_cents IS NULL OR bid_price_cents >= 0`),
    // Phase 12 / Task 12.0 (D3) — non-negative per-BBL rate override.
    check(
      'operators_per_bbl_price_nonneg',
      sql`per_bbl_price_cents IS NULL OR per_bbl_price_cents >= 0`,
    ),
  ],
);

export type Operator = typeof operators.$inferSelect;
export type NewOperator = typeof operators.$inferInsert;
export const operatorSelectSchema = createSelectSchema(operators);
export const operatorInsertSchema = createInsertSchema(operators);

// company_sites + site_tanks RETIRED — Phase 26 / Task 26.2 (DEC-147). The old
// location model is replaced by hauler_locations + hauler_tanks + wells
// (packages/shared/src/db/schema/platform-catalog.ts). Dropped in migration 0057.
// The tenant-scoped `operators` table above is KEPT (billing still reads it; its
// retirement is a separate billing-model-unification task — see DEC-147).

/**
 * site_geofences — polygon geofences (master spec §36 + §86).
 *
 * RLS-enforced. site_id is POLYMORPHIC per master spec §86 — its target
 * depends on site_type:
 *   - 'PICKUP'   → company_sites.id
 *   - 'DISPOSAL' → disposal_facilities.id (Plan 02-04)
 *   - 'YARD'     → yards.yard_id (Plan 02-03 yards.ts; same plan)
 *
 * Polymorphism is enforced at the application layer (Phase 8 GEO-02 wires the
 * editor + validation), NOT via a DB FK constraint. site_id is declared as a
 * soft uuid column without `.references()`. The trade-off is acceptable for
 * Phase 2 scope; a Phase 23 ALTER could add a CHECK trigger if needed.
 *
 * polygon_geojson is a TEXT column holding a GeoJSON-shaped JSON string
 * (closed ring `[[lng1,lat1],[lng2,lat2],...,[lng1,lat1]]`). Phase 8 GEO-02
 * adds Zod `.refine()` validation for closed-ring + coordinate sanity.
 *
 * dwell_time_seconds (default 60) and accuracy_threshold_ft (default 50) are
 * Phase 8 GEO-03 / GEO-04 tuning knobs — driver must remain inside the polygon
 * for ≥ N seconds AND have GPS accuracy ≤ M feet before auto-arrival fires.
 *
 * UNIQUE NOT enforced — multi-zone sites (tank battery + load-out pad on the
 * same wellsite) need multiple geofences per site_id. Phase 8 GEO-02 wires
 * the editing UX.
 *
 * created_by FK references users.id (sibling import). onDelete: set null —
 * preserves the geofence record after the creator's user row is deleted.
 */
export const siteGeofences = pgTable(
  'site_geofences',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // POLYMORPHIC — see module docstring. NO `.references()`.
    siteId: uuid('site_id').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    siteType: text('site_type').notNull(),
    // Phase 8 / DEC-080 — hybrid geometry. RADIUS (default) uses company_sites
    // center + radius_ft; POLYGON uses polygon_geojson. CHECK below.
    geofenceType: text('geofence_type').notNull().default('RADIUS'),
    // GeoJSON-shaped closed-ring polygon as JSON string. NULLABLE (DEC-080) — a
    // RADIUS geofence carries no polygon.
    polygonGeojson: text('polygon_geojson'),
    // Phase 8 / DEC-080 — radius (feet) for RADIUS geofences. Platform default 300ft.
    radiusFt: integer('radius_ft').default(300),
    // Phase 8 GEO-03 — driver must remain inside ≥ N seconds before auto-arrival.
    dwellTimeSeconds: integer('dwell_time_seconds').notNull().default(60),
    // Phase 8 GEO-04 — required GPS accuracy before auto-arrival fires.
    accuracyThresholdFt: integer('accuracy_threshold_ft').notNull().default(50),
    // Phase 8 / Task 8.2 / DEC-080 — dispatcher verification (§15). verified_by is
    // server-derived from the authenticated user, never client-supplied.
    isVerified: boolean('is_verified').notNull().default(false),
    verifiedAt: timestamp('verified_at', { withTimezone: true }),
    verifiedBy: uuid('verified_by').references(() => users.id, { onDelete: 'set null' }),
    isActive: boolean('is_active').notNull().default(true),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('site_geofences_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'site_geofences_import_batch_tenant_fk',
    }).onDelete('set null'),
    // Geofence lookup per site (multi-zone friendly — no UNIQUE).
    index('site_geofences_tenant_site_idx').on(t.tenantId, t.siteId),
    // Master spec §86 — site_type allowlist. Phase 24 / Task 24.6 (DEC-097) adds
    // TANK_BATTERY (geofence keyed by site_id = tank_battery_id).
    check(
      'site_geofences_site_type_check',
      sql`site_type IN ('PICKUP', 'DISPOSAL', 'YARD', 'TANK_BATTERY')`,
    ),
    // Phase 8 / DEC-080 — geofence_type allowlist.
    check('site_geofences_geofence_type_check', sql`geofence_type IN ('RADIUS', 'POLYGON')`),
    pgPolicy('site_geofences_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type SiteGeofence = typeof siteGeofences.$inferSelect;
export type NewSiteGeofence = typeof siteGeofences.$inferInsert;
export const siteGeofenceSelectSchema = createSelectSchema(siteGeofences);
export const siteGeofenceInsertSchema = createInsertSchema(siteGeofences);

/**
 * operator_haul_id_counters — atomic per-customer HaulId counter (master spec §14).
 *
 * Phase 6 / Plan 06-01 / D-22 step 2 / D-23 rationale.
 *
 * One row per operator. The `issueHaulId(tx, operatorId, tenantId)` helper
 * (Plan 06-03) atomically increments `next_value` via
 *   `INSERT ... ON CONFLICT (operator_id) DO UPDATE
 *      SET next_value = operator_haul_id_counters.next_value + 1
 *    RETURNING next_value`
 * holding the row lock until the surrounding capture transaction commits.
 *
 * Why a counter row, not a Postgres native sequence (D-23): native sequences
 * are NOT RLS-scoped (a compromised app_user could SELECT nextval() across
 * tenants), and `CREATE SEQUENCE haul_id_seq_<operator_id>` would produce
 * one sequence object per operator (DDL clutter). The counter-row approach
 * gives the same atomicity contract (row-lock + RETURNING) while staying
 * RLS-scoped and ddl-clean.
 *
 * PK is `operator_id` (one counter row per operator). `tenant_id` is
 * denormalised so the standard RLS predicate works without joining operators.
 * `next_value` is bigint with mode='number' — JS numbers safely represent
 * counter values up to 2^53; a single operator issuing one HaulId per second
 * would take ~285 million years to overflow.
 *
 * RLS posture: pgPolicy `operator_haul_id_counters_isolation` mirrors the
 * verbatim `operators_isolation` analog above. The SQL migration 0005 step 2
 * creates the same-named DB-side policy.
 */
export const operatorHaulIdCounters = pgTable(
  'operator_haul_id_counters',
  {
    operatorId: uuid('operator_id')
      .primaryKey()
      .references(() => operators.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Master spec §14 — counter is bigint so the column can store any plausible
    // operator's lifetime ticket volume. mode='number' returns a JS number (not
    // bigint) from drizzle queries — JS Number safely represents values up to
    // 2^53 - 1 which is several orders of magnitude beyond any realistic
    // per-operator ticket count.
    nextValue: bigint('next_value', { mode: 'number' }).notNull().default(1),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Per-tenant index — supports tenant-scoped queries (e.g. "list all counters
    // for this tenant"). RLS-isolation policy below uses tenant_id directly.
    index('operator_haul_id_counters_tenant_idx').on(t.tenantId),
    pgPolicy('operator_haul_id_counters_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type OperatorHaulIdCounter = typeof operatorHaulIdCounters.$inferSelect;
export type NewOperatorHaulIdCounter = typeof operatorHaulIdCounters.$inferInsert;
export const operatorHaulIdCounterSelectSchema = createSelectSchema(operatorHaulIdCounters);
export const operatorHaulIdCounterInsertSchema = createInsertSchema(operatorHaulIdCounters);
